'use client';
import React, { useState } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import { useToast } from '@/context/ToastContext';

interface SellTemplateProps {
  page: any;
  meta: any;
  slug: string;
}

export default function SellTemplate({ page, meta }: SellTemplateProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    emirate: 'Dubai',
    area: '',
    plotType: 'Residential',
    plotSize: '',
    askingPrice: '',
    sellImmediately: 'Yes',
    preferredContact: 'Phone',
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Please fill out all mandatory fields.');
      return;
    }

    setLoading(true);
    try {
      await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'sell',
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          details: {
            emirate: formData.emirate,
            area: formData.area,
            plotType: formData.plotType,
            plotSize: formData.plotSize,
            askingPrice: formData.askingPrice,
            sellImmediately: formData.sellImmediately,
            preferredContact: formData.preferredContact,
          },
        }),
      });
      setSubmitted(true);
      toast.success('Plot listing details submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Jost, sans-serif' }}>
      <Header />

      {/* Main Content Area - Padding top matches Header height */}
      <div className="pt-[100px] pb-20 bg-white">
        <div className="container mx-auto px-6 max-w-[1280px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Column - Copy content exactly matching screenshot */}
            <div className="lg:col-span-6 space-y-6 pt-2">
              <h1 className="text-[32px] md:text-[38px] font-black text-[#1a1a1a] leading-tight uppercase tracking-tight">
                Sell Your Land With Plotae – The UAE&apos;s Trusted Land Platform
              </h1>

              <div className="space-y-5 text-slate-600 text-[14px] leading-relaxed font-medium">
                <p>
                  Looking to sell your land in Dubai, Abu Dhabi, Sharjah, or anywhere in the UAE? At
                  Plotae.com, we specialize in helping landowners connect with serious buyers and
                  high-value investors. Whether you&apos;re selling a residential plot, commercial
                  land, or a prime investment property, our platform ensures your listing reaches
                  genuine investors who are ready to buy.
                </p>
                <p>
                  Unlike generic property portals, Plotae is the UAE&apos;s only land-focused
                  marketplace, designed exclusively for land investments. With our expertise and
                  verified investor database, your land gets positioned for maximum visibility,
                  faster sales, and better value.
                </p>
              </div>

              {/* Bullet Points Section */}
              <div className="space-y-4 pt-2">
                <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                  Why Sell With Plotae?
                </h3>
                <ul className="space-y-3.5">
                  <li className="flex items-start gap-2.5 text-[13px] font-medium text-slate-600">
                    <span className="text-sky-500 text-sm mt-0.5">💎</span>
                    <span>
                      <strong>UAE&apos;s only land-focused platform</strong> – specialized,
                      targeted, and trusted.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5 text-[13px] font-medium text-slate-600">
                    <span className="text-sky-500 text-sm mt-0.5">💎</span>
                    <span>
                      <strong>Strong investor database</strong> – from real estate developers to
                      global buyers.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5 text-[13px] font-medium text-slate-600">
                    <span className="text-sky-500 text-sm mt-0.5">💎</span>
                    <span>
                      <strong>Market intelligence & insights</strong> – we guide you with pricing
                      trends, area performance, and demand forecasts.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5 text-[13px] font-medium text-slate-600">
                    <span className="text-sky-500 text-sm mt-0.5">💎</span>
                    <span>
                      <strong>Fast & transparent transactions</strong> – we connect you with genuine
                      buyers and streamline the selling process.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="space-y-4 pt-2 text-[14px] leading-relaxed text-slate-600 font-medium">
                <p>
                  When you choose Plotae, you&apos;re not just listing your land—you&apos;re tapping
                  into a dedicated marketplace built exclusively for land investments in the UAE.
                </p>
                <p className="flex items-center gap-2 text-[13px] font-black text-[#a07818] uppercase tracking-wider">
                  <span>👉</span>
                  <span>
                    List your plot today and let us help you unlock the true value of your land
                  </span>
                </p>
              </div>
            </div>

            {/* Right Column - Soft Green Mockup Form box */}
            <div className="lg:col-span-6">
              <div className="bg-[#d2e9b8] p-8 md:p-9 shadow-sm rounded-none border border-[#c1dc9e]">
                <h2 className="text-[20px] font-black text-[#006838] uppercase tracking-tight leading-snug mb-6">
                  List Your Land Today – Connect with Serious Buyers
                </h2>

                {submitted ? (
                  <div className="py-12 bg-white/70 backdrop-blur-sm text-center space-y-5 animate-in fade-in zoom-in-95 duration-500 p-6">
                    <div className="w-16 h-16 bg-[#006838] rounded-full flex items-center justify-center mx-auto text-white shadow-md">
                      <svg
                        className="w-8 h-8 animate-bounce"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                      Details Received Successfully
                    </h3>
                    <p className="text-slate-500 text-xs max-w-sm mx-auto leading-relaxed font-semibold">
                      Your plot details have been privately listed on Plotae! An advisor will reach
                      out to you shortly.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="px-6 py-2.5 bg-[#006838] hover:bg-[#00502b] text-white text-xs font-black uppercase tracking-wider transition-all rounded-none"
                    >
                      List Another Plot
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Row 1: Your Name (First & Last name split) */}
                    <div>
                      <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                        Your Name *
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="first name"
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] transition-all rounded-none"
                        />
                        <input
                          type="text"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="last name"
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] transition-all rounded-none"
                        />
                      </div>
                    </div>

                    {/* Row 2: Email & Phone Number */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="email id"
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] transition-all rounded-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="phone number with country code"
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] transition-all rounded-none"
                        />
                      </div>
                    </div>

                    {/* Row 3: Emirate & Area */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                          Emirate
                        </label>
                        <select
                          name="emirate"
                          value={formData.emirate}
                          onChange={handleChange}
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] cursor-pointer transition-all rounded-none"
                        >
                          <option value="Dubai">Dubai</option>
                          <option value="Abu Dhabi">Abu Dhabi</option>
                          <option value="Sharjah">Sharjah</option>
                          <option value="Ajman">Ajman</option>
                          <option value="Ras Al Khaimah">Ras Al Khaimah</option>
                          <option value="Fujairah">Fujairah</option>
                          <option value="Umm Al Quwain">Umm Al Quwain</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                          Area
                        </label>
                        <input
                          type="text"
                          name="area"
                          value={formData.area}
                          onChange={handleChange}
                          placeholder="location"
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] transition-all rounded-none"
                        />
                      </div>
                    </div>

                    {/* Row 4: Plot Type & Plot Size */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                          Plot Type
                        </label>
                        <select
                          name="plotType"
                          value={formData.plotType}
                          onChange={handleChange}
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] cursor-pointer transition-all rounded-none"
                        >
                          <option value="Residential">Residential</option>
                          <option value="Commercial">Commercial</option>
                          <option value="Mixed-Use">Mixed-Use</option>
                          <option value="Industrial">Industrial</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                          Plot Size
                        </label>
                        <input
                          type="text"
                          name="plotSize"
                          value={formData.plotSize}
                          onChange={handleChange}
                          placeholder="plot size in sq.ft"
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] transition-all rounded-none"
                        />
                      </div>
                    </div>

                    {/* Row 5: Asking Price & Immediate Selling Select */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                          Asking Price
                        </label>
                        <input
                          type="text"
                          name="askingPrice"
                          value={formData.askingPrice}
                          onChange={handleChange}
                          placeholder="Dhs"
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] transition-all rounded-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                          Are you looking to sell immediately?
                        </label>
                        <select
                          name="sellImmediately"
                          value={formData.sellImmediately}
                          onChange={handleChange}
                          className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] cursor-pointer transition-all rounded-none"
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    </div>

                    {/* Row 6: Preferred Contact Method */}
                    <div>
                      <label className="block text-[11px] font-black text-[#50514f] mb-1.5 uppercase tracking-wider">
                        Preferred Contact Method
                      </label>
                      <select
                        name="preferredContact"
                        value={formData.preferredContact}
                        onChange={handleChange}
                        className="w-full bg-white border border-transparent px-3.5 py-2.5 text-[13px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#006838] focus:border-[#006838] cursor-pointer transition-all rounded-none"
                      >
                        <option value="Phone">Phone</option>
                        <option value="Email">Email</option>
                        <option value="WhatsApp">WhatsApp</option>
                      </select>
                    </div>

                    {/* Row 7: Submit Button */}
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="bg-white hover:bg-slate-50 border border-transparent text-[#006838] font-bold px-6 py-2.5 shadow transition-all duration-200 uppercase text-[13px] rounded-none inline-flex items-center gap-2"
                      >
                        {loading ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-[#006838] border-t-transparent rounded-full animate-spin"></div>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <span>Submit</span>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
